/**
 * TargetInConeCondition - behavior tree condition checking target in view cone.
 */

import * as THREE from 'three';

import { EngineClass } from '../../../../systems/ClassRegistry.js';
import { BehaviorStatus } from '../../BehaviorNode.js';
import { ConditionEvaluator, type ConditionEvaluatorOptions } from '../ConditionEvaluator.js';

import type { Actor } from '../../../../actors/Actor.js';
import type { Blackboard } from '../../blackboard/Blackboard.js';

export interface TargetInConeConditionOptions extends ConditionEvaluatorOptions {
  targetActorKey?: string;
  targetPositionKey?: string;
  coneAngle?: number;
  maxDistance?: number;
}

@EngineClass('TargetInConeCondition')
export class TargetInConeCondition extends ConditionEvaluator {
  private targetActorKey?: string;
  private targetPositionKey?: string;
  private coneAngle: number;
  private maxDistance: number;

  constructor(options: TargetInConeConditionOptions = {}) {
    super({
      name: 'TargetInCone',
      ...options
    });

    this.targetActorKey = options.targetActorKey ?? 'PlayerActor';
    this.targetPositionKey = options.targetPositionKey;
    this.coneAngle = options.coneAngle ?? 60.0; // 60 degrees total cone
    this.maxDistance = options.maxDistance ?? 20.0;
  }

  protected override async onEvaluate(blackboard: Blackboard): Promise<BehaviorStatus> {
    const owner = this.getOwner(blackboard);
    if (!owner) {
      return BehaviorStatus.Failure;
    }

    // Get target position
    const targetPos = this.getTargetPosition(blackboard);
    if (!targetPos) {
      return BehaviorStatus.Failure;
    }

    // Get owner position and forward direction
    const ownerPosition = new THREE.Vector3();
    owner.rootComponent.getWorldPosition(ownerPosition);
    const ownerForward = owner.forwardDirection();

    // Calculate vector to target
    const toTarget = targetPos.clone().sub(ownerPosition);
    const distanceToTarget = toTarget.length();

    // Check distance first
    if (distanceToTarget > this.maxDistance) {
      return BehaviorStatus.Failure;
    }

    // Normalize the direction to target
    if (distanceToTarget < 0.001) {
      // Target is too close to determine direction
      return BehaviorStatus.Success;
    }
    toTarget.normalize();

    // Calculate the angle between forward direction and target direction
    const dot = ownerForward.dot(toTarget);
    const angleRadians = Math.acos(THREE.MathUtils.clamp(dot, -1, 1));
    const angleDegrees = THREE.MathUtils.radToDeg(angleRadians);

    // Check if target is within the cone (half-angle on each side)
    const halfConeAngle = this.coneAngle / 2;
    return angleDegrees <= halfConeAngle ? BehaviorStatus.Success : BehaviorStatus.Failure;
  }

  private getTargetPosition(blackboard: Blackboard): THREE.Vector3 | null {
    if (this.targetActorKey) {
      const actor = blackboard.getValue<Actor>(this.targetActorKey);
      if (actor) {
        const position = new THREE.Vector3();
        actor.rootComponent.getWorldPosition(position);
        return position;
      }
    }

    if (this.targetPositionKey) {
      const position = blackboard.getValue<THREE.Vector3>(this.targetPositionKey);
      if (position) {
        return position.clone();
      }
    }

    return null;
  }
}